
import type { Step } from "./tables"
import {array, string} from "../libraries/json-schema/schema"

export function assert(condition: boolean, msg: string): asserts condition {
  if (!condition) throw new Error(msg)
}

export type File = string
export type Folder = {
  folders: Record<string, Folder>
  files: Record<string, File>
}


export type Path = string[]
export const Path = array(string)

export function last<T>(a: T[]): T | undefined {
  return a[a.length - 1]
}

export function getFolder (d: Folder, path: Path) : Folder | undefined
export function getFolder (d: Folder, path: Path, createMissing : true) : Folder

export function getFolder (d: Folder, path: Path, createMissing = false) {
  let target = d
  for (let s of path){
    let next = target.folders[s]
    if (next === undefined) {
      if (createMissing){
        next = {folders: {}, files: {}}
        target.folders[s] = next
      } else {
        return undefined
      }
    }
    target = next as Folder
  }
  return target
}

export function getFile (d:Folder, path: Path){
  let folder = getFolder(d, path.slice(0, -1))
  if (!folder) return undefined
  return folder.files[path[path.length - 1]!]
}

export function createFile(d: Folder, path: Path){
  let folder = getFolder(d,path.slice(0,-1), true)!
  folder.files[path[path.length - 1]!] = ""
}

export function setFile(d: Folder, path: Path, content:string){
  let folder = getFolder(d, path.slice(0,-1))
  assert (folder!= undefined, "folder not found")
  folder.files[last(path)!] = content
}

export function deleteEmptyFolder(d:Folder, path: Path){
  if (path.length === 0) return
  let folder = getFolder(d, path)
  assert (folder!= undefined, "folder not found")
  if (Object.keys(folder.folders).length === 0 && Object.keys(folder.files).length === 0){
    let parentFolder = getFolder(d, path.slice(0,-1))
    if (parentFolder) delete parentFolder.folders[last(path)!]
    deleteEmptyFolder(d, path.slice(0,-1))
  }
}

export function deleteFile(d: Folder, path: Path){
  let folder = getFolder(d, path.slice(0,-1))
  assert (folder!= undefined, "folder not found")
  delete folder.files[last(path)!]
  deleteEmptyFolder(d, path.slice(0,-1))
}


export function editFile(state: Folder, path: Path, oldText: string, newText: string): Step {
  assert(oldText.length > 0, "old must be a non-empty string")
  const old = getFile(state, path)
  assert(old !== undefined, "File not found")
  const occurrences = old.split(oldText).length - 1
  assert(occurrences === 1, `old must occur exactly once; found ${occurrences}`)
  return {path, old, new: old.replace(oldText, newText)}
}

export function createFileStep(state: Folder, path: Path, content: string): Step {
  assert(getFile(state, path) === undefined, `File exists at ${path.join("/")}`)
  return {path, old: null, new: content}
}

export type ChangeKind = "create" | "update" | "delete"

export function changeKind(step: Step): ChangeKind {
  if (step.old === null) return "create"
  if (step.new === null) return "delete"
  return "update"
}

export function applyStep(state: Folder, step: Step) {
  assert(step.path.length !== 0, "step.path is required")
  assert(step.old !== null || step.new !== null, "change must create, update, or delete a file")

  const current = getFile(state, step.path)
  if (step.old === null) {
    assert(current === undefined, `create condition violated: file exists at ${step.path.join("/")}`)
    createFile(state, step.path)
    setFile(state, step.path, step.new!)
  } else {
    assert(current === step.old, `change condition violated: expected ${step.old}, got ${current}`)
    if (step.new === null) deleteFile(state, step.path)
    else setFile(state, step.path, step.new)
  }
}

function files(state: Folder): Map<string, {path: Path, content: string}> {
  const result = new Map<string, {path: Path, content: string}>()
  const visit = (folder: Folder, parent: Path) => {
    for (const name of Object.keys(folder.files).sort()) {
      const path = [...parent, name]
      result.set(JSON.stringify(path), {path, content: folder.files[name]!})
    }
    for (const name of Object.keys(folder.folders).sort()) visit(folder.folders[name]!, [...parent, name])
  }
  visit(state, [])
  return result
}

export function stepsToState(current: Folder, target: Folder): Step[] {
  const before = files(current)
  const after = files(target)
  const steps: Step[] = []

  for (const [key, file] of before) {
    if (!after.has(key)) steps.push({path: file.path, old: file.content, new: null})
  }
  for (const [key, file] of after) {
    const old = before.get(key)?.content
    if (old === undefined) {
      steps.push({path: file.path, old: null, new: file.content})
    } else if (old !== file.content) {
      steps.push({path: file.path, old, new: file.content})
    }
  }
  return steps
}
